import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'

import styled from '@emotion/styled'
import { selectHideProductsTitles } from '../../../../flux/selectors/layout'
import { selectProduct } from '../../../../flux/selectors/product'

import useOnScreen from '../../../ui/hooks/useOnScreen'
import formatPrice from '../../../../helpers/formatPrice'
import Card from './Card'
import productHasValidVideo from '../../../../helpers/productHasValidVideo'
import DrawerCard from '../ProductDrawer/DrawerCard'

const StyledRoot = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  height: '100%',
})

const ProductCard = ({
  category,
  containerRef,
  product: gridProduct,
  isSimilarProductsCarousel,
  children,
  onProductClick,
  onProductVisible,
}) => {
  const isImageLoaded = React.useRef(false)
  const isProductIntersected = React.useRef(false)

  const productInfo = useSelector(selectProduct(gridProduct.id))
  const hasVideo = productHasValidVideo(productInfo?.mediaType)
  const hideProductTitle = useSelector(selectHideProductsTitles)

  const src = useMemo(() => (
    productInfo?.settings?.gibCover?.small?.[0] || productInfo.settings?.gibCover?.default?.[0] || ''
  ), [gridProduct.id])

  const finalPrice = useMemo(() => {
    const settings = productInfo?.settings || {}

    const digits = settings.price_extended?.digits

    const currency = settings.price_extended?.currency_symbol
    const priceExtended = formatPrice(digits, currency)

    let { price_prefix: prefix, price_suffix: suffix } = settings
    prefix = prefix ? `${prefix} ` : ''
    suffix = suffix ? ` ${suffix}` : ''

    return (
      `${prefix}${priceExtended}${suffix}`
    )
  }, [gridProduct.id])

  const startingPrice = useMemo(() => {
    const digits = productInfo?.settings?.starting_price?.digits

    const currency = productInfo?.settings?.starting_price?.currency_symbol
    return formatPrice(digits, currency)
  }, [gridProduct.id])

  const ref = useRef()

  useOnScreen({
    target: ref,
    onEnterScreen: () => {
      isProductIntersected.current = true
      if (isImageLoaded.current) {
        onProductVisible(gridProduct, category)
      }
    },
    minTimeVisible: 1000,
  }, { threshold: 0.50, root: containerRef })

  const onImageLoaded = () => {
    isImageLoaded.current = true
    if (isProductIntersected.current) {
      onProductVisible(gridProduct, category)
    }
  }

  return (
    <StyledRoot>
      <DrawerCard
        productId={gridProduct.id}
        mediaType={gridProduct.mediaType}
      >
        <Card
          previewRef={ref}
          mediaSrc={src}
          title={!hideProductTitle ? productInfo.title : null}
          price={finalPrice}
          realPrice={startingPrice}
          availability={productInfo.settings.availability}
          onImageLoaded={onImageLoaded}
          onClick={() => onProductClick(gridProduct, category)}
          containerRef={containerRef}
          hasVideo={hasVideo}
          isSimilarProductsCarousel={isSimilarProductsCarousel}
        />
      </DrawerCard>
      { children || null }
    </StyledRoot>
  )
}

ProductCard.propTypes = {
  category: PropTypes.string,
  onProductVisible: PropTypes.func,
  onProductClick: PropTypes.func,
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    mediaType: PropTypes.string,
  }).isRequired,
  containerRef: PropTypes.shape({}),
  children: PropTypes.element,
  isSimilarProductsCarousel: PropTypes.bool,
}

ProductCard.defaultProps = {
  category: '',
  containerRef: null,
  onProductVisible: () => false,
  onProductClick: () => false,
  children: null,
  isSimilarProductsCarousel: false,
}

ProductCard.displayName = 'ProductCard'
ProductCard.whyDidYouRender = true

export default React.memo(ProductCard)
